import { type HTMLAttributes, forwardRef } from "react";
import { type VariantProps, tv } from "tailwind-variants";
import { Typography } from "../Typography";

const toggleStyles = tv({
	base: "flex items-center gap-2 transition-all",
	variants: {
		disabled: {
			true: "opacity-50 cursor-not-allowed",
			false: "cursor-pointer",
		},
		size: {
			sm: "scale-90",
			md: "scale-100",
			lg: "scale-110",
		},
		variant: {
			default: "",
			primary: "",
			dark: "",
		},
	},
	defaultVariants: {
		disabled: false,
		size: "md",
		variant: "default",
	},
});

const switchStyles = tv({
	base: "relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none",
	variants: {
		checked: {
			true: "bg-blue-500",
			false: "bg-neutral-300",
		},
		variant: {
			default: "",
			primary: "[&.checked]:bg-turquoise-500",
			dark: "[&.checked]:bg-neutral-800",
		},
	},
	defaultVariants: {
		checked: false,
		variant: "default",
	},
});

const thumbStyles = tv({
	base: "inline-block h-4 w-4 transform rounded-full bg-neutral-600 transition-transform",
	variants: {
		checked: {
			true: "translate-x-5",
			false: "translate-x-1",
		},
	},
	defaultVariants: {
		checked: false,
	},
});

type BaseToggleProps = Omit<
	HTMLAttributes<HTMLDivElement>,
	"size" | "onChange"
>;

export interface ToggleProps
	extends BaseToggleProps,
		VariantProps<typeof toggleStyles> {
	checked: boolean;
	onChange: (checked: boolean) => void;
	label?: string;
	labelPosition?: "left" | "right";
	disabled?: boolean;
}

export const Toggle = forwardRef<HTMLDivElement, ToggleProps>(
	(
		{
			checked,
			onChange,
			label,
			labelPosition = "left",
			disabled = false,
			size,
			variant,
			className,
			...rest
		},
		ref,
	) => {
		const handleClick = () => {
			if (!disabled) {
				onChange(!checked);
			}
		};

		const toggleClassName = toggleStyles({
			disabled,
			size,
			variant,
			className,
		});
		const switchClassName = switchStyles({ checked, variant });
		const thumbClassName = thumbStyles({ checked });

		const renderLabel = () =>
			label && (
				<Typography size="sm" color="light">
					{label}
				</Typography>
			);

		return (
			<div
				className={toggleClassName}
				onClick={handleClick}
				ref={ref}
				role="switch"
				aria-checked={checked}
				aria-label={label}
				tabIndex={disabled ? -1 : 0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						handleClick();
					}
				}}
				{...rest}
			>
				{labelPosition === "left" && renderLabel()}
				<div className={switchClassName}>
					<span className={thumbClassName} />
				</div>
				{labelPosition === "right" && renderLabel()}
			</div>
		);
	},
);
